import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState, type MutableRefObject, type ReactNode } from 'react'
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { useWorkspace } from '../context/useWorkspace'
import { requestWorkspaceSwitch } from '../services/workspaceSwitchService'
import { safeWorkspaceLanding } from '../services/workspaceSwitch/switchLanding'
import { scopedReadConstraints } from '../services/accessScopeService'
import {
  buildJoinUrl,
  createEmailInvite,
  createHelperPhoneInvite,
  isInviteExpired,
  listPendingInvitesForWorkspace,
  revokeInvite,
} from '../services/inviteService'
import type { PendingInvite, PendingInviteRole } from '../types/invites'
import type { WorkspaceRole } from '../types/workspace'
import type { MemberControls, MemberControlsSettings } from '../types/memberControls'
import { formatRoleLabel } from '../utils/roleLabels'
import { HelperSchoolJobsSection } from './Invites/HelperSchoolJobsSection'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import { AppDialog, Button, Checkbox, ConfirmActionModal, LeaveWorkspaceConfirmModal, RailChipTabs, RailPageShell, SeatsStrip, StatusChip, TextInput, Toggle } from './Common'
import { SettingsLinkChip } from './Common/SettingsLinkChip'
import type { RailChipTabItem } from './Common'
import { Pressable } from './Common/Pressable'
import { Select } from './Common/Select'
import { PhoneNumberInput } from './Common/PhoneNumberInput'
import { BottomSheet } from './Common/BottomSheet'
import { endWorkspaceMember, leaveWorkspaceAndPurgeOutbox } from '../services/memberService'
import { getWhatsappInviteInfo } from '../services/whatsappService'
import { waMeInviteLink, waMeLink } from '../utils/whatsappLinks'
import { ConfirmDialog } from './DetailedView/ConfirmDialog'
import { setHelperAppAccess } from '../services/helperAppAccessService'
import { useFieldPassesModel } from './Invites/useFieldPassesModel'
import { FieldPassesListing } from './Invites/FieldPassesListing'
import { FieldPassesSidecar } from './Invites/FieldPassesSidecar'
import { JoinCodesPanel } from './Invites/JoinCodesPanel'
import { JoinWithCodeDialog } from './JoinCodes/JoinWithCodeDialog'
import { WorkspacePermissionManager } from './Admin/WorkspacePermissionManager'
import type { WorkspacePermissionManagerSaveHandle } from './Admin/WorkspacePermissionManager'
import { MemberProfileModal } from './Members/MemberProfileModal'
import { MemberControlsSheet } from './Member/controls/MemberControlsSheet'
import { ControlProfilesManager } from './Member/controls/ControlProfilesManager'
import { MemberBulkControlsBar } from './Member/controls/MemberBulkControlsBar'
import { AccessScopePanel } from './Members/AccessScopePanel'
import { AccessScopeEditor } from './Members/AccessScopeEditor'
import { TimelineContext } from '../context/timelineContextCore'
import type { TimelineElement } from '../types/timelineElement'
import { TIMELINE_BOARD_KEYS, type AccessScope, type NodeGrant, type TimelineBoardKey } from '../types/schema'
import { getWorkspaceLimitValue } from '../utils/workspaceLimits'
import { isWorkspaceRole, normalizeBoardWorkspaceRole, roleCanPerform } from '../utils/workspacePermissions'
import { isValidSupportedE164Phone } from '../utils/phoneNumber'
import { isPersonalWorkspace } from '../utils/workspaceKind'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import { HELPER_APK_DOWNLOAD_URL, MEMBER_APK_DOWNLOAD_URL } from '../services/nativeUpdateService'
import { isNoSignInPreviewActive } from '../debug'
import {
  DEBUG_ADMIN_COUNTERPARTY,
  DEBUG_EDITOR_USER,
  DEBUG_HELPER_PHONE_E164,
  DEBUG_HELPER_USER,
  DEBUG_VIEWER_USER,
} from '../debug/fixtures'
import { useNls } from '../nls/nlsProvider'
import type { SettingsNlsT } from './Settings/SettingsNls'
import {
  DEFAULT_HELPER_APP_LOCALE,
  HELPER_APP_LOCALE_OPTIONS,
  normalizeHelperAppLocale,
} from '../services/helper/languageRegistryService'
import type { NlsLocale } from '../nls/nlsTypes'

import {
  TAB_STORAGE_KEY,
  getTabStorageKey,
  isCategoryAllowed,
  resolveStoredCategory,
} from './Invites/invitesCategoryModel'

export type CategoryKey = 'MEMBERS' | 'JOIN_CODES' | 'INVITES' | 'HELPERS' | 'FIELD_PASSES' | 'PERMISSIONS' | 'GENERATE'
export type InviteTarget = 'MEMBER' | 'HELPER'
export type InvitesHubV2Handle = {
  openCreatePanel: (target?: InviteTarget) => void
  openNewFieldPass: () => void
}

export type MobileRail = CategoryKey

type InvitesHubV2Props = {
  isMobile?: boolean
  /** Pins the mobile shell to one purpose-built rail. Desktop stays unchanged. */
  mobileRail?: MobileRail
  /** Lets an outer mobile surface own the page hero. */
  mobileHideOwnHeader?: boolean
  /** Optional bridge for the Member-only permission footer. */
  onPermissionDirtyChange?: (isDirty: boolean) => void
  permissionSaveHandle?: MutableRefObject<WorkspacePermissionManagerSaveHandle | null>
  onOpenWorkspaceSettings?: () => void
}

export type ActivePerson = {
  id: string
  uid: string | null
  email: string
  displayName: string
  role: WorkspaceRole
  customRoleId: string | null
  boardRoles: Partial<Record<TimelineBoardKey, WorkspaceRole>>
  joinedAtMs: number | null
  lastActiveMs: number | null
  mobileNumber: string | null
  taskCount: number
  credits: number
  dailyCreditAllowance: number
  creditsConsumedToday: number
  photoURL: string | null
  helperAppLocale: NlsLocale | null
  canDelegateFieldPass: boolean
  controls?: MemberControlsSettings | null
  effectiveControls?: MemberControls | null
  eduJobs?: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  eduRouteIds?: string[]
  eduRollSessionIds?: string[]
  whatsappLinkStatus?: 'LINKED' | 'NOT_YET' | 'STOPPED' | null
  appAccess?: boolean
  notifyVia?: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY'
  verifiedAt?: string | null
  verifiedBy?: string | null
}

type StageItem =
  | { kind: 'PENDING'; id: string; invite: PendingInvite }
  | { kind: 'MEMBER'; id: string; person: ActivePerson }
  | { kind: 'HELPER'; id: string; person: ActivePerson }

const DEBUG_INVITE_RIGHTS_KEY = 'invites_hub_debug_invite_rights'
const MEMBER_LINK_ROLES: WorkspaceRole[] = ['READONLY', 'VIEWER', 'EDITOR']
const EMPTY_ELEMENTS: TimelineElement[] = []
const DEFAULT_ACCESS_SCOPE: AccessScope = { mode: 'ALL', grants: {} }

function isAccessScopeEqual(a: AccessScope | null | undefined, b: AccessScope | null | undefined): boolean {
  const normA = !a || a.mode === 'ALL' ? { mode: 'ALL' as const, grants: {} } : a
  const normB = !b || b.mode === 'ALL' ? { mode: 'ALL' as const, grants: {} } : b
  if (normA.mode !== normB.mode) return false
  const aGrants = normA.grants ?? {}
  const bGrants = normB.grants ?? {}
  const aKeys = Object.keys(aGrants)
  const bKeys = Object.keys(bGrants)
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    if (aGrants[k] !== bGrants[k]) return false
  }
  return true
}
const CATEGORY_META: Record<CategoryKey, { label: string; description: string }> = {
  GENERATE: { label: 'Invites', description: 'Create a member email invite or a helper phone invite.' },
  INVITES: { label: 'Invites', description: 'Outgoing invitations. Pick one to edit; create a new one from the right panel.' },
  MEMBERS: { label: 'Active Members', description: 'Admins, editors, and viewers in this workspace.' },
  HELPERS: { label: 'Active Helpers', description: 'Helper accounts connected through the mobile flow.' },
  FIELD_PASSES: { label: 'Field Passes', description: 'Temporary burner access for assigned work.' },
  PERMISSIONS: { label: 'Permissions', description: 'Workspace action policy and custom roles.' },
  JOIN_CODES: { label: 'Join Codes', description: 'Generate and manage staff join codes.' },
}

const getCategoryLabel = (key: CategoryKey, t: SettingsNlsT): string => {
  if (key === 'INVITES' || key === 'GENERATE') return t('NLSInvitesCategoryInvites')
  if (key === 'MEMBERS') return t('NLSInvitesCategoryMembers')
  if (key === 'HELPERS') return t('NLSInvitesCategoryHelpers')
  if (key === 'FIELD_PASSES') return t('NLSInvitesCategoryFieldPasses')
  if (key === 'JOIN_CODES') return t('NLSInvitesCategoryJoinCodes')
  return t('NLSInvitesCategoryPermissions')
}

const CATEGORY_ICONS: Partial<Record<Exclude<CategoryKey, 'GENERATE'>, ReactNode>> = {
  INVITES: <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />,
  MEMBERS: <GroupRoundedIcon sx={{ fontSize: 18 }} />,
  HELPERS: <HandshakeRoundedIcon sx={{ fontSize: 18 }} />,
  FIELD_PASSES: <BadgeRoundedIcon sx={{ fontSize: 18 }} />,
  JOIN_CODES: <KeyRoundedIcon sx={{ fontSize: 18 }} />,
}

const toMillis = (value: unknown): number | null => {
  if (value && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis()
  }
  return null
}

const formatRelative = (ms: number | null): string => {
  if (!ms) return 'No activity yet'
  const diff = Date.now() - ms
  const minutes = Math.round(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

const maskPhone = (value: string | null): string => {
  if (!value) return 'Mobile not shared'
  const visible = value.slice(-4)
  return `${value.slice(0, 3)}••••${visible}`
}

const formatInviteCountryLabel = (option: { label: string; code: string }): string => {
  const country = option.label.replace(/\s*\+\d+$/, '').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase()
  return country || option.code.replace('+', '')
}

const isPermissionError = (error: unknown): boolean =>
  error instanceof Error && /permission|insufficient/i.test(error.message)

const normalizeRole = (role: unknown): WorkspaceRole =>
  isWorkspaceRole(role) ? role : 'VIEWER'

const normalizeBoardRoles = (value: unknown): Partial<Record<TimelineBoardKey, WorkspaceRole>> => {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([board]) => TIMELINE_BOARD_KEYS.includes(board as TimelineBoardKey))
      .map(([board, role]) => [board, normalizeBoardWorkspaceRole(role)]),
  ) as Partial<Record<TimelineBoardKey, WorkspaceRole>>
}

const mapActivePerson = (snapshot: QueryDocumentSnapshot): ActivePerson | null => {
  const data = snapshot.data()
  if (data.status && data.status !== 'ACTIVE') return null
  const uid = typeof data.uid === 'string' && data.uid ? data.uid : typeof data.userId === 'string' && data.userId ? data.userId : null
  const email = typeof data.email === 'string' ? data.email : typeof data.user_email === 'string' ? data.user_email : ''
  const mobileNumber = typeof data.phone === 'string' && data.phone
    ? data.phone
    : typeof data.mobileNumber === 'string' && data.mobileNumber
      ? data.mobileNumber
      : null
  const rawWhatsappStatus = data.whatsappLink?.status
  const whatsappLinkStatus: 'LINKED' | 'NOT_YET' | 'STOPPED' | null =
    rawWhatsappStatus === 'LINKED' || rawWhatsappStatus === 'NOT_YET' || rawWhatsappStatus === 'STOPPED'
      ? rawWhatsappStatus
      : mobileNumber
        ? 'NOT_YET'
        : null
  const displayName = typeof data.displayName === 'string' && data.displayName
    ? data.displayName
    : email || mobileNumber || 'Workspace person'
  const normalizeCount = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0
  return {
    id: snapshot.id,
    uid,
    email,
    displayName,
    role: normalizeRole(data.role),
    customRoleId: typeof data.customRoleId === 'string' && data.customRoleId ? data.customRoleId : null,
    boardRoles: normalizeBoardRoles(data.boardRoles),
    joinedAtMs: toMillis(data.joinedAt),
    lastActiveMs: toMillis(data.lastReadAt) ?? toMillis(data.lastActiveAt) ?? toMillis(data.updatedAt) ?? toMillis(data.joinedAt),
    mobileNumber,
    taskCount: 0,
    credits: normalizeCount(data.credits),
    dailyCreditAllowance: normalizeCount(data.dailyCreditAllowance),
    creditsConsumedToday: normalizeCount(data.creditsConsumedToday),
    photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    helperAppLocale: typeof data.helperAppLocale === 'string' ? normalizeHelperAppLocale(data.helperAppLocale) : null,
    canDelegateFieldPass: data.canDelegateFieldPass === true,
    controls: (data.controls as MemberControlsSettings) ?? null,
    effectiveControls: (data.effectiveControls as MemberControls) ?? null,
    eduJobs: Array.isArray(data.eduJobs) ? (data.eduJobs as ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]) : [],
    eduRouteIds: Array.isArray(data.eduRouteIds) ? (data.eduRouteIds as string[]) : [],
    eduRollSessionIds: Array.isArray(data.eduRollSessionIds) ? (data.eduRollSessionIds as string[]) : [],
    whatsappLinkStatus,
    appAccess: typeof data.appAccess === 'boolean' ? data.appAccess : true,
    notifyVia: typeof data.notifyVia === 'string' && ['BOTH', 'APP_ONLY', 'WHATSAPP_ONLY'].includes(data.notifyVia)
      ? (data.notifyVia as 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY')
      : 'BOTH',
    verifiedAt: typeof data.verifiedAt === 'string' ? data.verifiedAt : null,
    verifiedBy: typeof data.verifiedBy === 'string' ? data.verifiedBy : null,
  }
}

const debugPersonDefaults = () => ({
  customRoleId: null as string | null,
  credits: 0,
  dailyCreditAllowance: 0,
  creditsConsumedToday: 0,
  photoURL: null as string | null,
  helperAppLocale: null as NlsLocale | null,
  canDelegateFieldPass: false,
  controls: null,
  effectiveControls: null,
  whatsappLinkStatus: null as 'LINKED' | 'NOT_YET' | 'STOPPED' | null,
  appAccess: true,
  notifyVia: 'BOTH' as 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY',
  verifiedAt: null as string | null,
  verifiedBy: null as string | null,
})

const getDebugPeople = (): ActivePerson[] => [
  {
    id: 'debug-admin-counterparty',
    uid: DEBUG_ADMIN_COUNTERPARTY.uid,
    email: DEBUG_ADMIN_COUNTERPARTY.email,
    displayName: DEBUG_ADMIN_COUNTERPARTY.displayName,
    role: 'ADMIN',
    boardRoles: {},
    joinedAtMs: Date.now() - 86_400_000,
    lastActiveMs: Date.now() - 12 * 60_000,
    mobileNumber: null,
    taskCount: 0,
    ...debugPersonDefaults(),
  },
  {
    id: 'debug-editor',
    uid: DEBUG_EDITOR_USER.uid,
    email: DEBUG_EDITOR_USER.email ?? '',
    displayName: DEBUG_EDITOR_USER.displayName ?? 'Debug Editor',
    role: 'EDITOR',
    boardRoles: {},
    joinedAtMs: Date.now() - 72_000_000,
    lastActiveMs: Date.now() - 45 * 60_000,
    mobileNumber: null,
    taskCount: 0,
    ...debugPersonDefaults(),
  },
  {
    id: 'debug-viewer',
    uid: DEBUG_VIEWER_USER.uid,
    email: DEBUG_VIEWER_USER.email ?? '',
    displayName: DEBUG_VIEWER_USER.displayName ?? 'Debug Viewer',
    role: 'VIEWER',
    boardRoles: {},
    joinedAtMs: Date.now() - 36_000_000,
    lastActiveMs: Date.now() - 2 * 60 * 60_000,
    mobileNumber: null,
    taskCount: 0,
    ...debugPersonDefaults(),
  },
  {
    id: 'debug-helper',
    uid: DEBUG_HELPER_USER.uid,
    email: DEBUG_HELPER_USER.email ?? '',
    displayName: DEBUG_HELPER_USER.displayName ?? 'Debug Helper',
    role: 'HELPER',
    boardRoles: {},
    joinedAtMs: Date.now() - 24_000_000,
    lastActiveMs: Date.now() - 8 * 60_000,
    mobileNumber: DEBUG_HELPER_PHONE_E164,
    taskCount: 3,
    ...debugPersonDefaults(),
  },
]

const StatusPill = ({ label, tone }: { label: string; tone: 'active' | 'pending' | 'member' | 'helper' | 'muted' }) => {
  const toneClass = {
    active: 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]',
    pending: 'border-[color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]',
    member: 'border-[color-mix(in_srgb,var(--accent-primary)_30%,transparent)] bg-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)] text-[var(--accent-strong)]',
    helper: 'border-[color-mix(in_srgb,var(--accent)_30%,transparent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-[var(--accent-strong)]',
    muted: 'border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text)]',
  }[tone]
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${toneClass}`}>{label}</span>
}

export const InvitesHubV2 = forwardRef<InvitesHubV2Handle, InvitesHubV2Props>(function InvitesHubV2({
  isMobile = false,
  mobileRail,
  mobileHideOwnHeader = false,
  onPermissionDirtyChange,
  permissionSaveHandle,
  onOpenWorkspaceSettings,
}, ref) {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const { activeWorkspaceId, activeRole, workspaces, canPerform, refreshWorkspaces } = useWorkspace()
  const [leavingWorkspace, setLeavingWorkspace] = useState<{ id: string; name: string } | null>(null)

  const handleLeftWorkspace = async () => {
    if (!leavingWorkspace) return
    const leftId = leavingWorkspace.id
    setLeavingWorkspace(null)
    if (refreshWorkspaces) await refreshWorkspaces()
    const remaining = workspaces.filter((entry) => entry.id !== leftId && !entry.isPreview)
    if (remaining.length > 0) {
      void requestWorkspaceSwitch({
        toWorkspaceId: remaining[0].id,
        source: 'MEMBERSHIP_ENDED',
        landing: safeWorkspaceLanding(remaining[0], 'DASHBOARD'),
      })
    } else {
      void requestWorkspaceSwitch({
        toWorkspaceId: null,
        source: 'MEMBERSHIP_ENDED',
      })
    }
  }
  const activeWorkspace = workspaces.find((entry) => entry.id === activeWorkspaceId)
  const isOwnerViewer = isWorkspaceOwner(activeWorkspace, currentUser?.uid)
  const canManageMembers = canPerform('workspace.manage_members')
  const canManageFieldPasses = canPerform('workspace.manage_field_passes')
  const canManageJoinCodes = isOwnerViewer || activeRole === 'ADMIN'
  const isDebug = isNoSignInPreviewActive()

  const canViewPermissionsRail = canPerform('workspace.manage_permissions')

  const [category, setCategory] = useState<CategoryKey>(() => {
    if (isMobile && mobileRail) {
      const allowed = isCategoryAllowed(mobileRail, {
        canManageInvites: canPerform('workspace.manage_invites'),
        canManageJoinCodes,
        canViewPermissions: canViewPermissionsRail,
        canManageFieldPasses,
      })
      if (allowed) return mobileRail
    }
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('invite') === 'helper') return isMobile ? 'GENERATE' : 'INVITES'
      if (params.get('member')) return 'MEMBERS'
    }
    if (typeof window === 'undefined') return 'MEMBERS'
    const stored = window.localStorage.getItem(getTabStorageKey(activeWorkspaceId)) ?? window.localStorage.getItem(TAB_STORAGE_KEY)
    return resolveStoredCategory(stored, {
      canManageInvites: canPerform('workspace.manage_invites'),
      canManageJoinCodes,
      canViewPermissions: canViewPermissionsRail,
      canManageFieldPasses,
    })
  })
  const [members, setMembers] = useState<ActivePerson[]>([])
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const isOwnerOrAdmin = isOwnerViewer || activeRole === 'ADMIN' || activeRole === 'OWNER'
  const [pendingAppAccessChange, setPendingAppAccessChange] = useState<{ person: ActivePerson; appAccess: boolean } | null>(null)
  const [appAccessError, setAppAccessError] = useState<string | null>(null)

  const executeSetAppAccess = async (person: ActivePerson, appAccess: boolean) => {
    if (!activeWorkspace?.id) return
    const targetUid = person.uid || person.id
    try {
      await setHelperAppAccess({
        workspaceId: activeWorkspace.id,
        uid: targetUid,
        appAccess,
      })
      setMembers((prev) =>
        prev.map((entry) =>
          entry.id === person.id
            ? { ...entry, appAccess }
            : entry,
        ),
      )
    } catch {
      setAppAccessError(t('NLSHelperAppAccessErrorGeneric'))
    }
  }

  const handleToggleAppAccess = (person: ActivePerson, newAppAccess: boolean) => {
    setAppAccessError(null)
    if (!newAppAccess) {
      setPendingAppAccessChange({ person, appAccess: false })
      return
    }
    void executeSetAppAccess(person, true)
  }
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [joinCodeDialogOpen, setJoinCodeDialogOpen] = useState(false)
  const timeline = useContext(TimelineContext)
  const timelineElements = timeline?.elements ?? EMPTY_ELEMENTS

  const [inviteTarget, setInviteTarget] = useState<InviteTarget>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('invite') === 'helper') return 'HELPER'
    }
    return 'MEMBER'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('invite') === 'helper') {
      params.delete('invite')
      const remainingSearch = params.toString()
      const newUrl = `${window.location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}${window.location.hash}`
      window.history.replaceState(window.history.state, '', newUrl)
    }
  }, [])
  const [memberLinkRole, setMemberLinkRole] = useState<WorkspaceRole>('VIEWER')
  const [memberScopeDraft, setMemberScopeDraft] = useState<AccessScope>(DEFAULT_ACCESS_SCOPE)

  const handleMemberRoleChange = (newRole: WorkspaceRole) => {
    setMemberLinkRole(newRole)
    if (newRole === 'VIEWER' || newRole === 'READONLY') {
      setMemberScopeDraft((curr) => {
        if (!curr || curr.mode !== 'RESTRICTED') return curr
        let changed = false
        const nextGrants: Record<string, NodeGrant> = {}
        for (const [k, v] of Object.entries(curr.grants ?? {})) {
          if (v === 'EDIT') {
            nextGrants[k] = 'VIEW'
            changed = true
          } else {
            nextGrants[k] = v
          }
        }
        return changed ? { mode: 'RESTRICTED', grants: nextGrants } : curr
      })
    }
  }

  const [inviteRightsRole, setInviteRightsRole] = useState<WorkspaceRole>('ADMIN')
  const [emailDraft, setEmailDraft] = useState('')
  const [phoneDraft, setPhoneDraft] = useState('')
  const [lastGeneratedLinks, setLastGeneratedLinks] = useState<{
    inviteUrl: string
    androidApkUrl: string | null
    bulkText: string
  } | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [profileTargetUid, setProfileTargetUid] = useState<string | null>(null)
  const [profilesManagerOpen, setProfilesManagerOpen] = useState(false)
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(() => new Set())
  const [pendingRemoval, setPendingRemoval] = useState<{ kind: 'MEMBER' | 'HELPER'; person: ActivePerson } | null>(null)
  const isPinnedMobileRail = isMobile && Boolean(mobileRail)

  // Field Pass state is hoisted here (rather than living inside the listing
  // component) so the middle stage and the right sidecar share it. The hook
  // stays inert when the tab is inactive because its fetches gate on
  // canManageFieldPasses + workspaceId — no queries fire until it's opened.
  const fieldPassesModel = useFieldPassesModel(activeWorkspaceId, canManageFieldPasses)

  useEffect(() => {
    if (!activeWorkspace) return
    const debugOverride = isDebug ? window.localStorage.getItem(DEBUG_INVITE_RIGHTS_KEY) : null
    const requiredRole = debugOverride === 'EDITOR' || debugOverride === 'VIEWER' || debugOverride === 'ADMIN'
      ? debugOverride
      : activeWorkspace.actionPermissions['workspace.manage_invites']
    setInviteRightsRole(requiredRole === 'EDITOR' || requiredRole === 'VIEWER' ? requiredRole : 'ADMIN')
  }, [activeWorkspace, isDebug])

  const canManageInvites = canPerform('workspace.manage_invites') || (isDebug && roleCanPerform(activeRole, inviteRightsRole))
  const canGenerateMemberLink = canManageInvites && emailDraft.trim().length > 0
  const canGenerateHelperLink = canManageInvites && isValidSupportedE164Phone(phoneDraft)

  const [whatsappInviteInfo, setWhatsappInviteInfo] = useState<{
    enabled: boolean
    sendingNumberE164: string | null
  } | null>(null)
  const [whatsappDialogPerson, setWhatsappDialogPerson] = useState<ActivePerson | null>(null)
  const [whatsappQrSvg, setWhatsappQrSvg] = useState<string>('')

  useEffect(() => {
    let isMounted = true
    if (activeWorkspaceId && canManageInvites) {
      getWhatsappInviteInfo(activeWorkspaceId)
        .then((info) => {
          if (isMounted) setWhatsappInviteInfo(info)
        })
        .catch(() => {
          if (isMounted) setWhatsappInviteInfo(null)
        })
    }
    return () => {
      isMounted = false
      setWhatsappInviteInfo(null)
    }
  }, [activeWorkspaceId, canManageInvites])

  useEffect(() => {
    let isMounted = true
    if (whatsappDialogPerson && whatsappInviteInfo?.sendingNumberE164) {
      const inviteUrl = waMeInviteLink(whatsappInviteInfo.sendingNumberE164)
      if (inviteUrl) {
        import('qrcode-generator')
          .then((mod) => {
            if (!isMounted) return
            const qrcodeFactory =
              (mod as { default?: typeof qrcode }).default || (mod as unknown as typeof qrcode)
            try {
              const qr = qrcodeFactory(0, 'M')
              qr.addData(inviteUrl)
              qr.make()
              setWhatsappQrSvg(qr.createSvgTag({ scalable: true }))
            } catch (err) {
              console.warn('[InvitesHubV2] failed to generate WhatsApp QR', err)
            }
          })
          .catch((err) => {
            console.warn('[InvitesHubV2] failed to load qrcode-generator', err)
          })
      }
    }
    return () => {
      isMounted = false
      setWhatsappQrSvg('')
    }
  }, [whatsappDialogPerson, whatsappInviteInfo?.sendingNumberE164])

  const renderWhatsappPersonButtons = (person: ActivePerson) => {
    if (!canManageInvites) return null
    const isViewer = person.uid === currentUser?.uid || person.id === currentUser?.uid
    if (isViewer) return null

    const showInvite = Boolean(whatsappInviteInfo?.sendingNumberE164)
    const showSendFromMine = Boolean(person.mobileNumber)

    if (!showInvite && !showSendFromMine) return null

    return (
      <>
        {showInvite && (
          <Button
            variant="secondary"
            size="sm"
            data-testid={`whatsapp-invite-btn-${person.id}`}
            onClick={(e) => {
              e.stopPropagation()
              setWhatsappDialogPerson(person)
            }}
          >
            {t('NLSWhatsappInviteAction')}
          </Button>
        )}
        {showSendFromMine && (
          <Button
            variant="secondary"
            size="sm"
            data-testid={`whatsapp-send-mine-btn-${person.id}`}
            onClick={(e) => {
              e.stopPropagation()
              const text = t('NLSWhatsappSendFromMineText', {
                name: person.displayName,
                sender: currentDisplayName || 'a team member',
                workspace: activeWorkspace?.name || 'our team',
              })
              const link = waMeLink(person.mobileNumber, text)
              if (link && typeof window !== 'undefined') {
                window.open(link, '_blank', 'noopener,noreferrer')
              }
            }}
          >
            {t('NLSWhatsappSendFromMineAction')}
          </Button>
        )}
      </>
    )
  }

  const selectCategory = (next: CategoryKey) => {
    setCategory(next)
    setSelectedId(null)
    setExpandedIds(new Set())
    setIsSelectMode(false)
    setSelectedMemberIds(new Set())
    setStatus(null)
    setError(null)
    if (next === 'HELPERS') setInviteTarget('HELPER')
    if (next === 'MEMBERS' || next === 'INVITES') setInviteTarget('MEMBER')
  }

  const [prevWorkspaceId, setPrevWorkspaceId] = useState(activeWorkspaceId)
  if (prevWorkspaceId !== activeWorkspaceId) {
    setPrevWorkspaceId(activeWorkspaceId)
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(getTabStorageKey(activeWorkspaceId)) ?? window.localStorage.getItem(TAB_STORAGE_KEY)
      const nextCategory = resolveStoredCategory(stored, {
        canManageInvites,
        canManageJoinCodes,
        canViewPermissions: canViewPermissionsRail,
        canManageFieldPasses,
      })
      if (nextCategory !== category) {
        setCategory(nextCategory)
      }
    }
  }

  const isAllowed = isCategoryAllowed(category, {
    canManageInvites,
    canManageJoinCodes,
    canViewPermissions: canViewPermissionsRail,
    canManageFieldPasses,
  })

  const isMobileRailAllowed = isPinnedMobileRail && mobileRail ? isCategoryAllowed(mobileRail, {
    canManageInvites,
    canManageJoinCodes,
    canViewPermissions: canViewPermissionsRail,
    canManageFieldPasses,
  }) : false

  if (isPinnedMobileRail && mobileRail && isMobileRailAllowed) {
    if (category !== mobileRail) {
      setCategory(mobileRail)
    }
  } else if (!isMobile && category === 'PERMISSIONS') {
    setCategory(canManageInvites ? 'INVITES' : 'MEMBERS')
  } else if (!isAllowed) {
    const fallback = category === 'JOIN_CODES' && canManageInvites ? 'INVITES' : 'MEMBERS'
    if (category !== fallback) {
      setCategory(fallback)
    }
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(getTabStorageKey(activeWorkspaceId), category)
    } catch {
      // localStorage may be unavailable in private contexts.
    }
  }, [activeWorkspaceId, category])

  const refresh = useCallback(async () => {
    if (!activeWorkspaceId) return
    setIsLoading(true)
    setError(null)
    if (isNoSignInPreviewActive()) {
      const rows = await listPendingInvitesForWorkspace(activeWorkspaceId)
      const people = getDebugPeople()
      setMembers(people)
      setInvites([...rows].sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)))
      setIsLoading(false)
      if (typeof window !== 'undefined') {
        const memberParam = new URLSearchParams(window.location.search).get('member')
        if (memberParam) {
          const match = people.find((entry) => entry.id === memberParam || entry.uid === memberParam)
          if (match) {
            setSelectedId(match.id)
            if (isMobile) setSheetOpen(true)
          }
        }
      }
      return
    }
    const [membersResult, invitesResult] = await Promise.all([
      getDocs(query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', activeWorkspaceId)))
        .then((snap) => ({ ok: true as const, snap }))
        .catch((caught: unknown) => ({ ok: false as const, error: caught })),
      listPendingInvitesForWorkspace(activeWorkspaceId)
        .then((rows) => ({ ok: true as const, rows }))
        .catch((caught: unknown) => ({ ok: false as const, error: caught })),
    ])

    if (membersResult.ok) {
      const hydrated = membersResult.snap.docs
        .map(mapActivePerson)
        .filter((entry): entry is ActivePerson => entry !== null)

      const withTaskCounts = await Promise.all(hydrated.map(async (person) => {
        if (!person.uid || person.role !== 'HELPER') return person
        try {
          const countSnapshot = await getCountFromServer(
            query(
              collection(firestoreDb, 'checklists'),
              where('workspaceId', '==', activeWorkspaceId),
              where('assigneeId', '==', person.uid),
              ...scopedReadConstraints(activeWorkspaceId),
            ),
          )
          return { ...person, taskCount: countSnapshot.data().count }
        } catch {
          return person
        }
      }))

      const sorted = withTaskCounts.sort((a, b) => a.displayName.localeCompare(b.displayName))
      setMembers(sorted)
      if (typeof window !== 'undefined') {
        const memberParam = new URLSearchParams(window.location.search).get('member')
        if (memberParam) {
          const match = sorted.find((entry) => entry.id === memberParam || entry.uid === memberParam)
          if (match) {
            setSelectedId(match.id)
            if (isMobile) setSheetOpen(true)
          }
        }
      }
    } else if (!isPermissionError(membersResult.error)) {
      setError(membersResult.error instanceof Error ? membersResult.error.message : 'Could not load members.')
    }

    if (invitesResult.ok) {
      setInvites([...invitesResult.rows].sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)))
    } else if (!isPermissionError(invitesResult.error)) {
      setError(invitesResult.error instanceof Error ? invitesResult.error.message : 'Could not load invites.')
    }

    setIsLoading(false)
  }, [activeWorkspaceId, isMobile])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const activeMembers = useMemo(() => members.filter((entry) => entry.role !== 'HELPER'), [members])
  const activeHelpers = useMemo(() => members.filter((entry) => entry.role === 'HELPER'), [members])
  const maxRoster = activeWorkspace?.workspaceLimits ? getWorkspaceLimitValue(activeWorkspace.workspaceLimits, 'maxWorkspaceMembers') : 0
  const maxPending = activeWorkspace?.workspaceLimits ? getWorkspaceLimitValue(activeWorkspace.workspaceLimits, 'maxPendingInvites') : 0
  const activeRosterCount = activeMembers.length + activeHelpers.length

  const stageItems = useMemo<StageItem[]>(() => {
    if (category === 'FIELD_PASSES' || category === 'PERMISSIONS' || category === 'GENERATE' || category === 'JOIN_CODES') return []
    if (category === 'INVITES') return invites.map((invite) => ({ kind: 'PENDING', id: invite.id, invite }))
    if (category === 'HELPERS') return activeHelpers.map((person) => ({ kind: 'HELPER', id: person.id, person }))
    return activeMembers.map((person) => ({ kind: 'MEMBER', id: person.id, person }))
  }, [activeHelpers, activeMembers, category, invites])

  const selectedItem = useMemo(() => {
    if (category === 'FIELD_PASSES' || category === 'PERMISSIONS' || category === 'GENERATE' || category === 'JOIN_CODES') return null
    // For the merged Invites tab, only surface a selection if the user
    // actively clicked one — the default sidecar view is the generate form.
    if (category === 'INVITES') {
      return selectedId ? stageItems.find((item) => item.id === selectedId) ?? null : null
    }
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('member')) {
      return selectedId ? stageItems.find((item) => item.id === selectedId) ?? null : null
    }
    return stageItems.find((item) => item.id === selectedId) ?? stageItems[0] ?? null
  }, [category, selectedId, stageItems])

  useEffect(() => {
    if (!selectedItem) {
      setSelectedId(null)
      return
    }
    if (!selectedId || !stageItems.some((item) => item.id === selectedId)) setSelectedId(selectedItem.id)
  }, [selectedId, selectedItem, stageItems])

  const openGeneratePanel = (target: InviteTarget) => {
    // The merged Invites tab keeps the generate form docked in the sidecar.
    // Switching to INVITES + clearing selection restores the default view;
    // choosing the target flips the Member/Helper segmented control there.
    selectCategory('INVITES')
    setSelectedId(null)
    setInviteTarget(target)
  }

  useImperativeHandle(ref, () => ({
    openCreatePanel: (target: InviteTarget = 'MEMBER') => {
      openGeneratePanel(target)
      setSheetOpen(true)
    },
    openNewFieldPass: () => {
      selectCategory('FIELD_PASSES')
      fieldPassesModel.startCreate()
      setSheetOpen(true)
    },
  }))

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const createInviteBase = (options: { skipPendingLimit?: boolean } = {}) => {
    if (!activeWorkspace || !currentUser) throw new Error('Select a workspace and sign in first.')
    if (!canManageInvites) throw new Error('You do not have invite rights for this workspace.')
    if (!options.skipPendingLimit && maxPending > 0 && invites.length >= maxPending) {
      throw new Error(`This workspace has reached its ${maxPending.toLocaleString()} pending invite limit.`)
    }
    return {
      workspaceId: activeWorkspace.id,
      workspaceName: activeWorkspace.name,
      workspaceSlug: activeWorkspace.slug ?? null,
      invitedBy: currentUser.uid,
      invitedByName: currentDisplayName || currentUser.email || null,
    }
  }

  const copyText = async (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(value)
    }
  }

  const buildInviteShareText = (target: InviteTarget, webLink: string): string => {
    if (target === 'MEMBER') {
      return [
        `Open your Timelining workspace invite in a browser: ${webLink}`,
        `Member app download (Android): ${MEMBER_APK_DOWNLOAD_URL}`,
      ].join('\n')
    }
    return [
      `Helper app download: ${HELPER_APK_DOWNLOAD_URL}`,
      `Helper registration link: ${webLink}`,
    ].join('\n')
  }

  const buildPendingInviteCopyText = (invite: PendingInvite): string => {
    const webLink = buildJoinUrl(invite.token).web
    return buildInviteShareText(invite.role === 'HELPER' ? 'HELPER' : 'MEMBER', webLink)
  }

  const generateLink = async (target: InviteTarget, forceNew = false) => {
    setBusyAction('link')
    setError(null)
    setStatus(null)
    try {
      const role: WorkspaceRole = target === 'HELPER' ? 'HELPER' : memberLinkRole
      const recipient = target === 'HELPER' ? phoneDraft.trim() : emailDraft.trim().toLowerCase()
      if (target === 'MEMBER' && !recipient) throw new Error('Enter the member email before generating a link.')
      if (target === 'HELPER' && !isValidSupportedE164Phone(recipient)) {
        throw new Error('Choose a country code and enter the helper phone number before generating a link.')
      }
      const reusableLinks = invites.filter((invite) =>
        invite.status === 'PENDING' &&
        invite.role === role &&
        invite.recipient === recipient &&
        invite.type === (target === 'HELPER' ? 'PHONE' : 'EMAIL') &&
        (target === 'HELPER' || isAccessScopeEqual(invite.accessScope, memberScopeDraft))
      )
      const existing = !forceNew ? reusableLinks[0] ?? null : null
      if (forceNew && currentUser) {
        await Promise.all(reusableLinks.map((invite) => revokeInvite(invite.id, currentUser.uid)))
      }
      const invite = existing ?? (target === 'HELPER'
        ? await createHelperPhoneInvite({ ...createInviteBase(), role: 'HELPER', phoneE164: recipient })
        : await createEmailInvite({ ...createInviteBase(), role, email: recipient, accessScope: memberScopeDraft }))
      const link = buildJoinUrl(invite.token).web
      const shareText = buildInviteShareText(target, link)
      const androidApkUrl = target === 'MEMBER' ? MEMBER_APK_DOWNLOAD_URL : HELPER_APK_DOWNLOAD_URL
      setLastGeneratedLinks({
        inviteUrl: link,
        androidApkUrl: androidApkUrl || null,
        bulkText: shareText,
      })
      try {
        await copyText(shareText)
        setStatus(existing ? 'Existing addressed invite link copied.' : forceNew ? 'Prior addressed invite invalidated. New link copied.' : 'Addressed invite link copied.')
      } catch {
        setStatus('Addressed invite link generated. Copy it from the sidecar.')
      }
      if (!existing) {
        if (target === 'HELPER') {
          setPhoneDraft('')
        } else {
          setEmailDraft('')
          setMemberScopeDraft(DEFAULT_ACCESS_SCOPE)
        }
      }
      if (!existing) await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not generate invite link.')
    } finally {
      setBusyAction(null)
    }
  }

  const sendEmailInvite = async () => {
    const email = emailDraft.trim()
    if (!email) return
    setBusyAction('email')
    setError(null)
    setStatus(null)
    try {
      const invite = await createEmailInvite({
        ...createInviteBase(),
        email,
        role: memberLinkRole,
        accessScope: memberScopeDraft,
      })
      const link = buildJoinUrl(invite.token).web
      const shareText = buildInviteShareText('MEMBER', link)
      setLastGeneratedLinks({
        inviteUrl: link,
        androidApkUrl: MEMBER_APK_DOWNLOAD_URL || null,
        bulkText: shareText,
      })
      const baseStatus = invite.resent ? t('NLSInvitesInviteResent') : t('NLSInvitesInviteSent')
      try {
        await copyText(shareText)
        setStatus(`${baseStatus} Browser invite link copied.`)
      } catch {
        setStatus(`${baseStatus} Copy the browser invite link from the sidecar.`)
      }
      setEmailDraft('')
      setMemberScopeDraft(DEFAULT_ACCESS_SCOPE)
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create email invite.')
    } finally {
      setBusyAction(null)
    }
  }

  const sendHelperPhoneInvite = async () => {
    const phoneE164 = phoneDraft.trim()
    if (!isValidSupportedE164Phone(phoneE164)) {
      setError('Choose a country code and enter the helper phone number digits.')
      setStatus(null)
      return
    }
    setBusyAction('phone')
    setError(null)
    setStatus(null)
    try {
      const existingForPhone = invites.some((invite) =>
        invite.type === 'PHONE' &&
        invite.role === 'HELPER' &&
        invite.status === 'PENDING' &&
        invite.recipient === phoneE164
      )
      if (!existingForPhone && maxPending > 0 && invites.length >= maxPending) {
        throw new Error(`This workspace has reached its ${maxPending.toLocaleString()} pending invite limit.`)
      }
      const invite = await createHelperPhoneInvite({
        ...createInviteBase({ skipPendingLimit: true }),
        role: 'HELPER',
        phoneE164,
      })
      const link = buildJoinUrl(invite.token).web
      const shareText = buildInviteShareText('HELPER', link)
      setPhoneDraft('')
      setLastGeneratedLinks({
        inviteUrl: link,
        androidApkUrl: HELPER_APK_DOWNLOAD_URL || null,
        bulkText: shareText,
      })
      await copyText(shareText).catch(() => undefined)
      setStatus(invite.notifiedUserId
        ? 'Helper phone invite created. The registered user was notified; app download and fallback link copied.'
        : 'Helper phone invite created. App download and fallback link copied; it will appear when that number signs in.')
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create helper phone invite.')
    } finally {
      setBusyAction(null)
    }
  }

  const revokePendingInvite = async (invite: PendingInvite) => {
    if (!currentUser) return
    setBusyAction(`revoke:${invite.id}`)
    setError(null)
    setStatus(null)
    try {
      await revokeInvite(invite.id, currentUser.uid)
      setStatus('Invite invalidated.')
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not revoke invite.')
    } finally {
      setBusyAction(null)
    }
  }

  const updatePersonRole = async (person: ActivePerson, role: WorkspaceRole) => {
    if (person.role === 'OWNER' || role === 'OWNER') return
    if (person.role === 'ADMIN' && !isOwnerViewer) return
    setBusyAction(`role:${person.id}`)
    setError(null)
    try {
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.map((entry) => entry.id === person.id ? { ...entry, role } : entry))
        setStatus('Role updated.')
        return
      }
      await updateDoc(doc(firestoreDb, 'workspace_members', person.id), { role })
      setStatus('Role updated.')
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update role.')
    } finally {
      setBusyAction(null)
    }
  }

  const updateBoardRole = async (person: ActivePerson, board: TimelineBoardKey, role: WorkspaceRole) => {
    if (person.role === 'OWNER' || role === 'OWNER') return
    if (person.role === 'ADMIN' && !isOwnerViewer) return
    setBusyAction(`board:${person.id}:${board}`)
    setError(null)
    try {
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.map((entry) => entry.id === person.id ? { ...entry, boardRoles: { ...entry.boardRoles, [board]: role } } : entry))
        setStatus('Board permission updated.')
        return
      }
      await updateDoc(doc(firestoreDb, 'workspace_members', person.id), {
        boardRoles: { ...person.boardRoles, [board]: role },
      })
      setStatus('Board permission updated.')
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update board permission.')
    } finally {
      setBusyAction(null)
    }
  }

  const handleConfirmRemoval = async () => {
    if (!pendingRemoval || !activeWorkspaceId) return
    const { kind, person } = pendingRemoval
    const targetUserId = person.uid
    if (!targetUserId) {
      setError('This person has no bound account yet — cannot remove.')
      setPendingRemoval(null)
      return
    }
    setBusyAction(`remove-member:${person.id}`)
    setError(null)
    try {
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.filter((entry) => entry.id !== person.id))
        setStatus(`${kind === 'HELPER' ? 'Helper' : 'Member'} removed.`)
        setSelectedId(null)
        setPendingRemoval(null)
        return
      }
      await endWorkspaceMember({
        workspaceId: activeWorkspaceId,
        targetUserId,
      })
      setStatus(`${kind === 'HELPER' ? 'Helper' : 'Member'} removed.`)
      setSelectedId(null)
      setPendingRemoval(null)
      await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not remove this person.')
    } finally {
      setBusyAction(null)
    }
  }

  const allotCredits = async (person: ActivePerson, rawValue: string) => {
    const parsed = Number.parseInt(rawValue, 10)
    if (!Number.isFinite(parsed) || parsed === 0) return
    setBusyAction(`credits:${person.id}`)
    setError(null)
    try {
      const nextCredits = Math.max(0, person.credits + parsed)
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.map((entry) => entry.id === person.id ? { ...entry, credits: nextCredits } : entry))
      } else {
        await updateDoc(doc(firestoreDb, 'workspace_members', person.id), { credits: nextCredits })
      }
      setStatus(`${parsed > 0 ? 'Allotted' : 'Reclaimed'} ${Math.abs(parsed)} credit${Math.abs(parsed) === 1 ? '' : 's'}.`)
      if (!isNoSignInPreviewActive()) await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update credits.')
    } finally {
      setBusyAction(null)
    }
  }

  const updatePersonHelperAppLocale = async (person: ActivePerson, localeInput: string) => {
    const helperAppLocale = normalizeHelperAppLocale(localeInput)
    if (helperAppLocale === person.helperAppLocale) return
    setBusyAction(`helper-locale:${person.id}`)
    setError(null)
    try {
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.map((entry) => entry.id === person.id ? { ...entry, helperAppLocale } : entry))
      } else {
        await updateDoc(doc(firestoreDb, 'workspace_members', person.id), {
          helperAppLocale,
          helperAppLocaleUpdatedAt: serverTimestamp(),
          helperAppLocaleUpdatedBy: currentUser?.uid ?? null,
        })
      }
      setStatus('Helper app language updated.')
      if (!isNoSignInPreviewActive()) await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update Helper app language.')
    } finally {
      setBusyAction(null)
    }
  }

  const updatePersonFieldPassDelegation = async (person: ActivePerson, canDelegateFieldPass: boolean) => {
    if (canDelegateFieldPass === person.canDelegateFieldPass) return
    setBusyAction(`helper-field-pass:${person.id}`)
    setError(null)
    try {
      if (isNoSignInPreviewActive()) {
        setMembers((current) => current.map((entry) => entry.id === person.id ? { ...entry, canDelegateFieldPass } : entry))
      } else {
        await updateDoc(doc(firestoreDb, 'workspace_members', person.id), {
          canDelegateFieldPass,
          canDelegateFieldPassUpdatedAt: serverTimestamp(),
          canDelegateFieldPassUpdatedBy: currentUser?.uid ?? null,
        })
      }
      setStatus(canDelegateFieldPass ? t('NLSInvitesHelperFieldPassDelegationEnabled') : t('NLSInvitesHelperFieldPassDelegationDisabled'))
      if (!isNoSignInPreviewActive()) await refresh()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSInvitesHelperFieldPassDelegationFailed'))
    } finally {
      setBusyAction(null)
    }
  }


  if (!activeWorkspaceId || !activeWorkspace) {
    return <section className="railPageCard invitesHub min-h-[50dvh] p-8 text-sm text-[var(--muted)]">{t('NLSInvitesSelectWorkspace')}</section>
  }

  if (isPersonalWorkspace(activeWorkspace)) {
    return (
      <section className="railPageCard invitesHub min-h-[50dvh] p-8 text-center text-[var(--text-strong)]">
        <p className="text-[11px] uppercase tracking-widest text-[var(--muted)]">{t('NLSInvitesHubEyebrow')}</p>
        <h1 className="mt-2 text-2xl font-semibold text-[var(--text-strong)]">{t('NLSInvitesPersonalCannotInvite')}</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-[var(--muted)]">{t('NLSInvitesPersonalCannotInviteBody')}</p>
      </section>
    )
  }

  const categoryEntries = ([
    ['MEMBERS', activeMembers.length],
    ['JOIN_CODES', 0],
    ['INVITES', invites.length],
    ['HELPERS', activeHelpers.length],
    ['FIELD_PASSES', 0],
  ] as Array<[CategoryKey, number]>)
    .filter(([key]) => {
      return isCategoryAllowed(key, {
        canManageInvites,
        canManageJoinCodes,
        canViewPermissions: canViewPermissionsRail,
        canManageFieldPasses,
      })
    })

  const statusBanner = (error || status) ? (
    <div className={`rounded-xl border px-3 py-2 text-xs ${error ? 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]' : 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'}`}>
      {error ?? status}
    </div>
  ) : null

  const rosterCapacityPercent = Math.min(100, Math.round((activeRosterCount / Math.max(1, maxRoster)) * 100))
  const pendingCapacityPercent = Math.min(100, Math.round((invites.length / Math.max(1, maxPending)) * 100))

  const mobileCapacityStats = (
    <>
      <div className="flex flex-col gap-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-3 py-2">
        <span className="text-[var(--muted)]">{t('NLSInvitesRosterCapacity')}</span>
        <strong className="text-[var(--text-strong)]">{activeRosterCount.toLocaleString()} / {maxRoster.toLocaleString()}</strong>
      </div>
      <div className="flex flex-col gap-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-3 py-2">
        <span className="text-[var(--muted)]">{t('NLSInvitesPendingCapacity')}</span>
        <strong className="text-[var(--text-strong)]">{invites.length.toLocaleString()} / {maxPending.toLocaleString()}</strong>
      </div>
    </>
  )

  const sidecarCapacityStats = (
    <div className="invitesSidecarCapacityGrid" aria-label="Invite capacity">
      <div className="invitesSidecarCapacityMetric">
        <span className="invitesSidecarCapacityLabel">{t('NLSInvitesRosterCapacity')}</span>
        <strong className="invitesSidecarCapacityValue">{activeRosterCount.toLocaleString()} / {maxRoster.toLocaleString()}</strong>
        <span className="invitesSidecarCapacityTrack" aria-hidden="true">
          <span style={{ width: `${rosterCapacityPercent}%` }} />
        </span>
      </div>
      <div className="invitesSidecarCapacityMetric">
        <span className="invitesSidecarCapacityLabel">{t('NLSInvitesPendingCapacity')}</span>
        <strong className="invitesSidecarCapacityValue">{invites.length.toLocaleString()} / {maxPending.toLocaleString()}</strong>
        <span className="invitesSidecarCapacityTrack" aria-hidden="true">
          <span style={{ width: `${pendingCapacityPercent}%` }} />
        </span>
      </div>
    </div>
  )

  const categoryNav = (
    <RailChipTabs<CategoryKey>
      ariaLabel={t('NLSInvitesAriaMembersCategory')}
      value={category}
      onChange={selectCategory}
      items={categoryEntries.map<RailChipTabItem<CategoryKey>>(([key, count]) => ({
        key,
        label: getCategoryLabel(key, t),
        subtitle: key === 'JOIN_CODES' ? t('NLSInvitesCategoryJoinCodesCaption') : CATEGORY_META[key].description,
        icon: CATEGORY_ICONS[key as Exclude<CategoryKey, 'GENERATE'>],
        count: key === 'INVITES' || key === 'MEMBERS' || key === 'HELPERS' ? count : undefined,
      }))}
    />
  )

  const mainList = (
    <>
      {!isPinnedMobileRail ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="invitesMobileRailTitle">{getCategoryLabel(category, t)}</h2>
            <p className="invitesMobileRailCaption">
              {category === 'PERMISSIONS'
                ? CATEGORY_META.PERMISSIONS.description
                : category === 'FIELD_PASSES'
                  ? CATEGORY_META.FIELD_PASSES.description
                  : category === 'JOIN_CODES'
                    ? t('NLSInvitesCategoryJoinCodesCaption')
                    : `${stageItems.length} records`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {category === 'MEMBERS' && canManageMembers ? (
              <>
                {!isSelectMode ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setIsSelectMode(true)}
                    data-testid="members-select-mode-btn"
                  >
                    {t('NLSControlsSelectMode')}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const shownMemberIds = stageItems
                          .filter((item) => item.kind === 'MEMBER')
                          .map((item) => item.person.id)
                        setSelectedMemberIds(new Set(shownMemberIds))
                      }}
                      data-testid="members-select-all-btn"
                    >
                      {t('NLSControlsSelectAllShown')}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        setIsSelectMode(false)
                        setSelectedMemberIds(new Set())
                      }}
                      data-testid="members-cancel-select-btn"
                    >
                      {t('NLSConfirmActionCancel')}
                    </Button>
                  </>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setProfilesManagerOpen(true)}
                  data-testid="open-profiles-manager-btn"
                >
                  {t('NLSControlsProfilesAction')}
                </Button>
              </>
            ) : null}
            {isLoading ? <StatusPill label={t('NLSInvitesStatusLoading')} tone="muted" /> : null}
          </div>
        </div>
      ) : null}

      {category === 'PERMISSIONS' ? (
        canViewPermissionsRail ? (
          <div className="invitesPermissionsStack flex flex-col gap-6">
            <WorkspacePermissionManager
              workspace={activeWorkspace}
              onDirtyChange={isPinnedMobileRail ? onPermissionDirtyChange : undefined}
              saveHandle={isPinnedMobileRail ? permissionSaveHandle : undefined}
              hideInlineSaveButton={isPinnedMobileRail}
            />
          </div>
        ) : (
          <div className="engagementEmpty">{t('NLSInvitesRequiresPermissionsAccess')}</div>
        )
      ) : category === 'FIELD_PASSES' ? (
        <FieldPassesListing
          model={fieldPassesModel}
          canManage={canManageFieldPasses}
          selectedPassId={fieldPassesModel.selectedPass?.id ?? null}
          onSelect={(pass) => fieldPassesModel.selectPass(pass.id)}
          hideCreateAction={isPinnedMobileRail}
        />
      ) : category === 'JOIN_CODES' ? (
        <JoinCodesPanel />
      ) : !isLoading && stageItems.length === 0 ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--border)] bg-[var(--surface)] p-8 text-center">
          <h3 className="text-base font-semibold text-[var(--text-strong)]">
            {category === 'HELPERS' ? 'No helpers active yet' : category === 'MEMBERS' ? 'No active members yet' : 'No pending invites'}
          </h3>
          <p className="max-w-sm text-sm text-[var(--muted)]">
            {category === 'HELPERS'
              ? canManageInvites ? 'Generate a helper invite link and share it with the person who will work from the mobile Helper shell.' : 'Ask a workspace admin for invite rights to add helpers.'
              : category === 'INVITES'
                ? canManageInvites ? (isPinnedMobileRail ? 'No pending invites. Tap Invite to send one.' : 'No invitations awaiting claim. Create one from the right side panel.') : 'Ask a workspace admin for invite rights to create invitations.'
                : canManageInvites ? 'Open the Invites tab in the left rail to create the next one.' : 'Ask a workspace admin for invite rights to create invitations.'}
          </p>
          {canManageInvites && category !== 'INVITES' ? (
            <Button variant="primary" onClick={() => openGeneratePanel(category === 'HELPERS' ? 'HELPER' : 'MEMBER')}>
              {category === 'HELPERS' ? 'Invite your first Helper' : 'Invite a member'}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {stageItems.map((item) => {
            const expanded = expandedIds.has(item.id)
            const selected = selectedItem?.id === item.id
            const title = item.kind === 'PENDING'
              ? item.invite.type === 'LINK' ? `${item.invite.role} join link` : item.invite.recipient
              : item.person.displayName
            const subtitle = item.kind === 'PENDING'
              ? `Invited ${formatRelative(item.invite.createdAt?.toMillis?.() ?? null)}`
              : item.kind === 'HELPER'
                ? maskPhone(item.person.mobileNumber)
                : item.person.email || 'No email on file'

            return (
              <article key={item.id} className={`rounded-lg border p-3 transition-colors ${selected ? 'border-[color-mix(in_srgb,var(--accent)_50%,transparent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]' : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] hover:border-[var(--border)]'}`}>
                <div className="flex items-start gap-3">
                  {isSelectMode && item.kind === 'MEMBER' ? (
                    <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedMemberIds.has(item.person.id)}
                        onChange={(checked) => {
                          setSelectedMemberIds((prev) => {
                            const next = new Set(prev)
                            if (checked) next.add(item.person.id)
                            else next.delete(item.person.id)
                            return next
                          })
                        }}
                        aria-label={`Select ${item.person.displayName || item.person.email || 'member'}`}
                        data-testid={`select-member-${item.person.id}`}
                      />
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <Pressable className="flex w-full items-start justify-between gap-3 text-left hover:!bg-transparent hover:!border-transparent hover:!text-inherit hover:!shadow-none" onClick={() => { setSelectedId(item.id); toggleExpanded(item.id); if (isMobile && canManageInvites) setSheetOpen(true) }}>
                      <span>
                        <span className="block text-sm font-semibold text-[var(--text-strong)]">{title}</span>
                        <span className="mt-1 block text-[11px] text-[var(--muted)]">{subtitle}</span>
                      </span>
                      <span className="flex shrink-0 flex-wrap justify-end gap-2">
                        {item.kind === 'PENDING' ? (
                          isInviteExpired(item.invite) ? (
                            <StatusPill label={t('NLSInvitesStatusExpired')} tone="muted" />
                          ) : (
                            <StatusPill label={t('NLSInvitesStatusInvited')} tone="pending" />
                          )
                        ) : (
                          <StatusPill label={t('NLSInvitesStatusActive')} tone="active" />
                        )}
                        {item.kind === 'HELPER' ? <StatusPill label={t('NLSInvitesStatusHelper')} tone="helper" /> : item.kind === 'MEMBER' ? <StatusPill label={item.person.role} tone="member" /> : <StatusPill label={item.invite.type} tone="muted" />}
                        {(item.kind === 'MEMBER' || item.kind === 'HELPER') && item.person.mobileNumber ? (
                          <StatusPill
                            label={
                              item.person.whatsappLinkStatus === 'LINKED'
                                ? t('NLSWhatsappChipLinked')
                                : item.person.whatsappLinkStatus === 'STOPPED'
                                  ? t('NLSWhatsappChipStopped')
                                  : t('NLSWhatsappChipNotYet')
                            }
                            tone={
                              item.person.whatsappLinkStatus === 'LINKED'
                                ? 'active'
                                : item.person.whatsappLinkStatus === 'STOPPED'
                                  ? 'pending'
                                  : 'muted'
                            }
                          />
                        ) : null}
                      </span>
                    </Pressable>
                    {expanded ? (
                      <div className="mt-3 grid gap-2 border-t border-[var(--border-subtle)] pt-3 text-[11px] text-[var(--muted)] sm:grid-cols-2">
                        {item.kind === 'PENDING' ? (
                          <>
                            <span>Role: {item.invite.role}</span>
                            <span>Status: {item.invite.status.toLowerCase()}</span>
                            <span>Type: {item.invite.type.toLowerCase()}</span>
                            <span>Token: {item.invite.token.slice(0, 8)}…</span>
                          </>
                        ) : item.kind === 'HELPER' ? (
                          <>
                            <span>Mobile: {maskPhone(item.person.mobileNumber)}</span>
                            <span>Last active: {formatRelative(item.person.lastActiveMs)}</span>
                            <span>Task status: Assigned tasks: {item.person.taskCount}</span>
                            <span>UID: {item.person.uid ? `${item.person.uid.slice(0, 8)}…` : 'Not bound'}</span>
                            <div className="col-span-full pt-1 flex flex-wrap items-center gap-2" data-testid={`helper-status-strip-${item.person.id}`}>
                              <StatusChip tone="neutral" size="sm" data-testid={`helper-notify-via-chip-${item.person.id}`}>
                                {`Notifications: ${
                                  item.person.notifyVia === 'APP_ONLY'
                                    ? t('NLSHelperNotifyViaAppOnly')
                                    : item.person.notifyVia === 'WHATSAPP_ONLY'
                                      ? t('NLSHelperNotifyViaWhatsappOnly')
                                      : t('NLSHelperNotifyViaBoth')
                                }`}
                              </StatusChip>
                              <StatusChip
                                tone={item.person.verifiedAt ? 'success' : 'warning'}
                                size="sm"
                                data-testid={`helper-verification-chip-${item.person.id}`}
                              >
                                {item.person.verifiedAt ? t('NLSHelperVerificationVerified') : t('NLSHelperVerificationWaiting')}
                              </StatusChip>
                              {isOwnerOrAdmin ? (
                                <div className="flex items-center gap-1.5 ml-auto">
                                  <span className="text-xs text-[var(--text-muted)]">{t('NLSHelperAppAccessToggleLabel')}</span>
                                  <Toggle
                                    checked={item.person.appAccess !== false}
                                    onChange={(checked) => handleToggleAppAccess(item.person, checked)}
                                    aria-label={t('NLSHelperAppAccessToggleLabel')}
                                    data-testid={`helper-app-access-toggle-${item.person.id}`}
                                  />
                                </div>
                              ) : null}
                            </div>
                            {appAccessError && (
                              <div className="col-span-full text-xs text-[var(--danger)]" data-testid="helper-app-access-error">
                                {appAccessError}
                              </div>
                            )}
                            <div className="col-span-full pt-2 flex flex-wrap justify-end gap-2">
                              {renderWhatsappPersonButtons(item.person)}
                              {(item.person.uid === currentUser?.uid || item.person.id === currentUser?.uid) ? (
                                <Button
                                  variant="tertiary"
                                  tone="danger"
                                  size="sm"
                                  title={t('NLSInvitesLeaveWorkspaceAction')}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (activeWorkspace) {
                                      setLeavingWorkspace({ id: activeWorkspace.id, name: activeWorkspace.name })
                                    }
                                  }}
                                >
                                  {t('NLSInvitesLeaveWorkspaceAction')}
                                </Button>
                              ) : (
                                <Button
                                  variant="tertiary"
                                  tone="danger"
                                  size="sm"
                                  disabled={busyAction === `remove-member:${item.person.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setPendingRemoval({ kind: 'HELPER', person: item.person })
                                  }}
                                >
                                  {t('NLSInvitesRemoveHelperAction')}
                                </Button>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <span>Email: {item.person.email || 'No email'}</span>
                            <span>Role: {item.person.role}</span>
                            <span>Last active: {formatRelative(item.person.lastActiveMs)}</span>
                            <span>Status: active</span>
                            <div className="col-span-full pt-2 flex flex-wrap justify-end gap-2">
                              {renderWhatsappPersonButtons(item.person)}
                              {(item.person.uid === currentUser?.uid || item.person.id === currentUser?.uid) ? (
                                <Button
                                  variant="tertiary"
                                  tone="danger"
                                  size="sm"
                                  title={t('NLSInvitesLeaveWorkspaceAction')}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (activeWorkspace) {
                                      setLeavingWorkspace({ id: activeWorkspace.id, name: activeWorkspace.name })
                                    }
                                  }}
                                >
                                  {t('NLSInvitesLeaveWorkspaceAction')}
                                </Button>
                              ) : (
                                <Button
                                  variant="tertiary"
                                  tone="danger"
                                  size="sm"
                                  disabled={
                                    item.person.role === 'OWNER'
                                    || (item.person.role === 'ADMIN' && !isOwnerViewer)
                                    || busyAction === `remove-member:${item.person.id}`
                                  }
                                  title={
                                    item.person.role === 'OWNER'
                                      ? t('NLSInvitesRemoveOwnerTooltip')
                                      : item.person.role === 'ADMIN' && !isOwnerViewer
                                        ? t('NLSInvitesAdminOwnerOnlyTooltip')
                                        : undefined
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (item.person.role === 'OWNER' || (item.person.role === 'ADMIN' && !isOwnerViewer)) return
                                    setPendingRemoval({ kind: 'MEMBER', person: item.person })
                                  }}
                                >
                                  {t('NLSInvitesRemoveMemberAction')}
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
      {category === 'MEMBERS' && isSelectMode && selectedMemberIds.size > 0 && activeWorkspace ? (
        <MemberBulkControlsBar
          workspaceId={activeWorkspace.id}
          selectedMembers={activeMembers
            .filter((p) => selectedMemberIds.has(p.id))
            .map((p) => ({
              id: p.id,
              uid: p.uid,
              role: p.role,
              displayName: p.displayName,
            }))}
          isOwnerViewer={isOwnerViewer}
          onClearSelection={() => {
            setSelectedMemberIds(new Set())
            setIsSelectMode(false)
          }}
          onSuccess={() => {
            void refresh()
          }}
        />
      ) : null}
    </>
  )

  const generatePanel = canManageInvites ? (
    <div className="invitesSidecarInviteCard" data-testid="invite-generate-stage">
      <section className="invitesSidecarCapacityCard" data-testid="invite-capacity-card">
        <header className="invitesSidecarCardHead">
          <span className="invitesSidecarCardEyebrow">Capacity</span>
          <strong className="invitesSidecarCardHeading">{activeWorkspace.name}</strong>
        </header>
        {sidecarCapacityStats}
      </section>

      <section className="invitesSidecarComposer" data-testid="invite-generate-panel">
        <header className="invitesSidecarCardHead">
          <span className="invitesSidecarCardEyebrow">
            {inviteTarget === 'MEMBER'
              ? <GroupRoundedIcon sx={{ fontSize: 14 }} />
              : <HandshakeRoundedIcon sx={{ fontSize: 14 }} />}
            <span>{t('NLSInvitesHubEyebrow')}</span>
          </span>
          <strong className="invitesSidecarCardHeading">{t('NLSInvitesGenerateTitle')}</strong>
          <p className="invitesSidecarCardSubtitle">
            {inviteTarget === 'MEMBER'
              ? 'Send an email invite to a teammate. They pick their role in the workspace on claim.'
              : 'Send a phone invite to a helper. They join the mobile Helper app with hard-coded rights.'}
          </p>
        </header>

        <div className="invitesSidecarSegmented invitesSidecarSegmented--full" role="group" aria-label={t('NLSInvitesGenerateTitle')}>
          <Pressable
            aria-pressed={inviteTarget === 'MEMBER'}
            onClick={() => setInviteTarget('MEMBER')}
            className={`invitesSidecarSegmentedBtn${inviteTarget === 'MEMBER' ? ' isActive' : ''}`}
            data-testid="invite-target-member"
          >
            <GroupRoundedIcon sx={{ fontSize: 15 }} />
            <span>{t('NLSInvitesTargetMember')}</span>
          </Pressable>
          <Pressable
            aria-pressed={inviteTarget === 'HELPER'}
            onClick={() => setInviteTarget('HELPER')}
            className={`invitesSidecarSegmentedBtn${inviteTarget === 'HELPER' ? ' isActive' : ''}`}
            data-testid="invite-target-helper"
          >
            <HandshakeRoundedIcon sx={{ fontSize: 15 }} />
            <span>{t('NLSInvitesTargetHelper')}</span>
          </Pressable>
        </div>

        {inviteTarget === 'MEMBER' ? (
          <div className="invitesSidecarComposerForm">
            <div className="invitesSidecarField">
              <span className="invitesSidecarFieldLabel">Role</span>
              <div className="invitesSidecarRolePicker">
                {MEMBER_LINK_ROLES.map((role) => (
                  <Button
                    key={role}
                    size="sm"
                    variant={memberLinkRole === role ? 'secondary' : 'tertiary'}
                    onClick={() => handleMemberRoleChange(role)}
                    data-testid={`invite-member-role-${role.toLowerCase()}`}
                  >
                    {role}
                  </Button>
                ))}
              </div>
              <span className="invitesSidecarFieldHint">The invitee joins with this workspace role.</span>
            </div>
            {(memberLinkRole === 'EDITOR' || memberLinkRole === 'VIEWER' || memberLinkRole === 'READONLY') && (
              <div className="invitesSidecarField" data-testid="invite-member-access-field">
                <span className="invitesSidecarFieldLabel">{t('NLSAccessScopeSectionTitle')}</span>
                <AccessScopeEditor
                  value={memberScopeDraft}
                  onChange={setMemberScopeDraft}
                  role={memberLinkRole}
                  elements={timelineElements}
                  disabled={!canManageInvites || busyAction === 'email' || busyAction === 'link'}
                />
                <span className="invitesSidecarFieldHint">{t('NLSAccessScopeInviteFieldHint')}</span>
              </div>
            )}
            <label className="invitesSidecarField">
              <span className="invitesSidecarFieldLabel">Recipient email</span>
              <TextInput
                type="email"
                value={emailDraft}
                onChange={setEmailDraft}
                placeholder="person@company.com"
                aria-label={t('NLSInvitesRecipientEmailLabel')}
                className="invitesSidecarControl invitesSidecarControl--email"
              />
            </label>
            <div className="invitesSidecarComposerActions">
              <Button variant="primary" disabled={!canGenerateMemberLink || busyAction === 'email'} onClick={() => void sendEmailInvite()}>
                <MailOutlineRoundedIcon sx={{ fontSize: 16 }} />
                {t('NLSInvitesActionSendEmailInvite')}
              </Button>
              <div className="invitesSidecarComposerActions__row">
                <Button variant="secondary" size="sm" disabled={busyAction === 'link' || !canGenerateMemberLink} onClick={() => void generateLink('MEMBER')} data-testid="invite-copy-link">
                  {t('NLSInvitesActionCopyInviteLink')}
                </Button>
                <Button variant="tertiary" size="sm" disabled={busyAction === 'link' || !canGenerateMemberLink} onClick={() => void generateLink('MEMBER', true)} data-testid="invite-generate-new">
                  {t('NLSInvitesActionInvalidateRegenerate')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="invitesSidecarComposerForm">
            <p className="invitesSidecarNote">
              Helper links are hard-coded to the HELPER role. Share the app download and registration link with the helper.
            </p>
            <label className="invitesSidecarField">
              <span className="invitesSidecarFieldLabel">Helper phone</span>
              <PhoneNumberInput
                value={phoneDraft}
                onChange={setPhoneDraft}
                disabled={!canManageInvites || busyAction === 'phone'}
                groupClassName="invitePhoneInputGroup invitesSidecarPhoneGroup"
                selectClassName="invitesSidecarControl invitesSidecarControl--dial"
                inputClassName="invitesSidecarControl invitesSidecarControl--phone"
                formatCountryLabel={formatInviteCountryLabel}
                testIdPrefix="invite-helper-phone"
              />
            </label>
            <div className="invitesSidecarComposerActions">
              <Button variant="primary" disabled={!canGenerateHelperLink || busyAction === 'phone'} onClick={() => void sendHelperPhoneInvite()}>
                <HandshakeRoundedIcon sx={{ fontSize: 16 }} />
                {t('NLSInvitesActionSendHelperInvite')}
              </Button>
              <div className="invitesSidecarComposerActions__row">
                <Button variant="secondary" size="sm" disabled={busyAction === 'link' || !canGenerateHelperLink} onClick={() => void generateLink('HELPER')} data-testid="invite-copy-link">
                  {t('NLSInvitesActionCopyInviteLink')}
                </Button>
                <Button variant="tertiary" size="sm" disabled={busyAction === 'link' || !canGenerateHelperLink} onClick={() => void generateLink('HELPER', true)} data-testid="invite-generate-new">
                  {t('NLSInvitesActionInvalidateRegenerate')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>

      {lastGeneratedLinks ? (
        <section className="invitesSidecarGeneratedLink lastGeneratedInviteCard rounded-2xl border border-[var(--border)] bg-[var(--surface-raised)] p-4" data-testid="invite-last-generated-card">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">
            {t('NLSInvitesLastGeneratedTitle')}
          </p>
          <p className="mt-1 text-xs text-[var(--muted)]">
            {t('NLSInvitesLastGeneratedHint')}
          </p>

          <div className="mt-3 grid gap-2">
            {/* Row 1 — workspace invite */}
            <div className="flex items-start justify-between gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-2">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">
                  {t('NLSInvitesLastGeneratedInviteLabel')}
                </p>
                <p className="mt-1 truncate font-mono text-[11px] text-[var(--text)]" title={lastGeneratedLinks.inviteUrl}>
                  {lastGeneratedLinks.inviteUrl}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void copyText(lastGeneratedLinks.inviteUrl).then(() => setStatus('Invite link copied.')).catch(() => setStatus('Copy unavailable.'))}
              >
                {t('NLSInvitesLastGeneratedCopyLink')}
              </Button>
            </div>

            {/* Row 2 — Android APK (conditional) */}
            {lastGeneratedLinks.androidApkUrl ? (
              <div className="flex items-start justify-between gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-2">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    {t('NLSInvitesLastGeneratedApkLabel')}
                  </p>
                  <p className="mt-1 truncate font-mono text-[11px] text-[var(--text)]" title={lastGeneratedLinks.androidApkUrl}>
                    {lastGeneratedLinks.androidApkUrl}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void copyText(lastGeneratedLinks.androidApkUrl!).then(() => setStatus('App download link copied.')).catch(() => setStatus('Copy unavailable.'))}
                >
                  {t('NLSInvitesLastGeneratedCopyLink')}
                </Button>
              </div>
            ) : null}
          </div>

          {/* Bulk copy */}
          <div className="mt-3 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void copyText(lastGeneratedLinks.bulkText).then(() => setStatus('Invite details copied.')).catch(() => setStatus('Copy unavailable.'))}
            >
              {t('NLSInvitesLastGeneratedCopyAll')}
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  ) : (
    <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--border)] bg-[var(--surface)] p-8 text-center">
      <h3 className="text-base font-semibold text-[var(--text-strong)]">{t('NLSInvitesRightsRequired')}</h3>
      <p className="max-w-sm text-sm text-[var(--muted)]">{t('NLSInvitesRightsRequiredBody')}</p>
    </div>
  )

  const renderPersonSidecar = (person: ActivePerson, kind: 'MEMBER' | 'HELPER') => {
    const customRole = person.customRoleId
      ? (activeWorkspace.customRoles ?? []).find((role) => role.id === person.customRoleId) ?? null
      : null
    return <MemberControlsSheet
      member={person}
      workspaceId={activeWorkspace.id}
      kind={kind}
      canManageMembers={canManageMembers}
      isOwnerViewer={isOwnerViewer}
      busyAction={busyAction}
      whatsappActions={renderWhatsappPersonButtons(person)}
      accessControls={
        person.uid && canManageMembers ? (
          <AccessScopePanel
            workspaceId={activeWorkspace.id}
            member={{
              uid: person.uid,
              displayName: person.displayName,
              role: person.role,
            }}
            canManage={canManageMembers}
          />
        ) : undefined
      }
      onRoleChange={(role) => void updatePersonRole(person, role)}
      onBoardPermissionChange={(board, role) => void updateBoardRole(person, board, role)}
      onCreditAllot={(value) => void allotCredits(person, value)}
      onControlsSaved={() => { void refresh() }}
      onViewProfile={() => person.uid && setProfileTargetUid(person.uid)}
      onRemove={() => setPendingRemoval({ kind, person })}
      removeDisabled={person.role === 'OWNER' || (person.role === 'ADMIN' && !isOwnerViewer)}
      removeTitle={
        person.role === 'OWNER'
          ? t('NLSInvitesRemoveOwnerTooltip')
          : person.role === 'ADMIN' && !isOwnerViewer
            ? t('NLSInvitesAdminOwnerOnlyTooltip')
            : undefined
      }
      removeLabel={kind === 'HELPER' ? t('NLSInvitesRemoveHelperAction') : t('NLSInvitesRemoveMemberAction')}
      customRole={customRole ? { name: customRole.name, color: customRole.color } : null}
      roleLabel={t('NLSInvitesLabelWorkspaceRole')}
      boardPermissionsLabel={t('NLSInvitesLabelBoardPermissions')}
      creditsLabel={t('NLSInvitesLabelCredits')}
      helperControls={kind === 'HELPER' ? <>
            <section className="invitesSidecarSection" data-testid="helper-app-language-control">
              <h4>{t('NLSInvitesHelperAppLanguageTitle')}</h4>
              <p className="mb-3 text-xs text-[var(--muted)]">{t('NLSInvitesHelperAppLanguageBody')}</p>
              <Select<NlsLocale>
                trigger="labeledPill"
                value={person.helperAppLocale ?? DEFAULT_HELPER_APP_LOCALE}
                options={HELPER_APP_LOCALE_OPTIONS.map((entry) => ({
                  value: entry.code,
                  label: `${entry.nativeLabel} · ${entry.label}${entry.rtl ? ' · RTL' : ''}`,
                }))}
                onChange={(locale) => void updatePersonHelperAppLocale(person, locale)}
                ariaLabel={t('NLSInvitesHelperAppLanguageAria')}
                disabled={!canManageMembers || busyAction === `helper-locale:${person.id}`}
              />
            </section>
            <section className="invitesSidecarSection" data-testid="helper-field-pass-delegation-control">
              <h4>{t('NLSInvitesHelperFieldPassDelegationTitle')}</h4>
              <p className="mb-3 text-xs text-[var(--muted)]">{t('NLSInvitesHelperFieldPassDelegationBody')}</p>
              <Toggle
                wrapperClassName="inviteEditToggle"
                checked={person.canDelegateFieldPass}
                disabled={!canManageMembers || busyAction === `helper-field-pass:${person.id}`}
                onChange={(next) => void updatePersonFieldPassDelegation(person, next)}
                aria-label={t('NLSInvitesHelperFieldPassDelegationAria')}
                label={person.canDelegateFieldPass ? t('NLSInvitesHelperFieldPassDelegationOn') : t('NLSInvitesHelperFieldPassDelegationOff')}
              />
            </section>
            {activeWorkspace && (activeWorkspace.modules?.teachingEnabled || activeWorkspace.modules?.trainingEnabled) ? (
              <HelperSchoolJobsSection
                workspaceId={activeWorkspace.id}
                helperUid={person.uid ?? ''}
                isEduWorkspace={Boolean(activeWorkspace.modules?.teachingEnabled || activeWorkspace.modules?.trainingEnabled)}
                canManage={canManageMembers}
                initialJobs={person.eduJobs}
                initialRouteIds={person.eduRouteIds}
                initialRollSessionIds={person.eduRollSessionIds}
                onSaved={() => { void refresh() }}
              />
            ) : null}
            <section className="invitesSidecarSection">
              <h4>Helper snapshot</h4>
              <p className="text-xs text-[var(--muted)]">Assigned tasks: {person.taskCount}</p>
            </section>
          </> : undefined}
    />
  }

  const renderPendingInviteSidecar = (invite: PendingInvite) => {
    // The pending sidecar wraps existing invite writers so an admin can act
    // on a selected pending invite without dropping back to the generate
    // form. Change-role and resend both re-run the create path (there's no
    // dedicated resend API), which is safe because the recipient is stable
    // and revoke-then-create keeps a single active invite per recipient.
    const isHelper = invite.role === 'HELPER'
    const isEmail = invite.type === 'EMAIL'
    const roleOptions: PendingInviteRole[] = isHelper ? ['HELPER'] : ['READONLY', 'VIEWER', 'EDITOR']

    const changeRole = async (nextRole: PendingInviteRole) => {
      if (!currentUser || !canManageInvites || nextRole === invite.role) return
      setBusyAction(`role:${invite.id}`)
      setError(null)
      setStatus(null)
      try {
        await revokeInvite(invite.id, currentUser.uid)
        const created = isHelper
          ? await createHelperPhoneInvite({ ...createInviteBase({ skipPendingLimit: true }), role: 'HELPER', phoneE164: invite.recipient })
          : await createEmailInvite({ ...createInviteBase({ skipPendingLimit: true }), role: nextRole, email: invite.recipient })
        setStatus(`Invite role updated to ${nextRole}. Copy the new link below.`)
        setSelectedId(created.id)
        await refresh()
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not change invite role.')
      } finally {
        setBusyAction(null)
      }
    }

    const resend = async () => {
      if (!currentUser || !canManageInvites) return
      setBusyAction(`resend:${invite.id}`)
      setError(null)
      setStatus(null)
      try {
        const created: PendingInvite & { resent?: boolean } = isHelper
          ? await createHelperPhoneInvite({ ...createInviteBase({ skipPendingLimit: true }), role: 'HELPER', phoneE164: invite.recipient })
          : await createEmailInvite({ ...createInviteBase({ skipPendingLimit: true }), role: invite.role, email: invite.recipient })
        const link = buildJoinUrl(created.token).web
        await copyText(buildInviteShareText(isHelper ? 'HELPER' : 'MEMBER', link)).catch(() => undefined)
        const baseStatus = created.resent ? t('NLSInvitesInviteResent') : t('NLSInvitesInviteSent')
        setStatus(isEmail ? `${baseStatus} Browser invite link copied.` : 'Sent again. Link copied.')
        setSelectedId(created.id)
        await refresh()
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not resend invite.')
      } finally {
        setBusyAction(null)
      }
    }

    const invalidateAndRegenerate = async () => {
      if (!currentUser || !canManageInvites) return
      setBusyAction(`regen:${invite.id}`)
      setError(null)
      setStatus(null)
      try {
        await revokeInvite(invite.id, currentUser.uid)
        const created = isHelper
          ? await createHelperPhoneInvite({ ...createInviteBase({ skipPendingLimit: true }), role: 'HELPER', phoneE164: invite.recipient })
          : await createEmailInvite({ ...createInviteBase({ skipPendingLimit: true }), role: invite.role, email: invite.recipient })
        const link = buildJoinUrl(created.token).web
        await copyText(buildInviteShareText(isHelper ? 'HELPER' : 'MEMBER', link)).catch(() => undefined)
        setStatus('Prior invite invalidated. New link copied.')
        setSelectedId(created.id)
        await refresh()
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not regenerate invite.')
      } finally {
        setBusyAction(null)
      }
    }

    return (
      <>
        <section className="invitesSidecarHero">
          <header className="invitesSidecarHeader">
            <div className="invitesSidecarAvatar">
              <span>{(invite.recipient || invite.role).charAt(0).toUpperCase()}</span>
            </div>
            <div className="invitesSidecarIdentity">
              <strong>{isHelper ? 'Helper invite' : 'Member invite'}</strong>
              <span>{invite.type === 'LINK' ? 'Open join link' : invite.recipient}</span>
              <div className="invitesSidecarChips">
                <StatusChip size="sm" tone="warning" className="invitesSidecarPresenceChip is-away">pending</StatusChip>
                <span className="invitesSidecarLastActive">{formatRoleLabel(t, invite.role)}</span>
              </div>
            </div>
          </header>
          <div className="invitesSidecarActions">
            <Button variant="secondary" size="sm" disabled={!canManageInvites} onClick={() => void copyText(buildPendingInviteCopyText(invite)).then(() => setStatus(invite.type === 'LINK' ? 'Invite links copied.' : 'Invite link copied.')).catch(() => setStatus('Copy unavailable.'))}>
              Copy Link
            </Button>
            <Button variant="tertiary" size="sm" disabled={!canManageInvites || busyAction === `revoke:${invite.id}`} onClick={() => void revokePendingInvite(invite)}>
              Revoke
            </Button>
          </div>
        </section>
        {!isHelper ? (
          <section className="invitesSidecarSection">
            <h4>Role</h4>
            <Select<PendingInviteRole>
              trigger="labeledPill"
              value={invite.role}
              options={roleOptions.map((role) => ({ value: role, label: formatRoleLabel(t, role) }))}
              onChange={(next) => void changeRole(next)}
              ariaLabel="Change invite role"
              disabled={!canManageInvites || busyAction === `role:${invite.id}`}
            />
            <p className="invitesSidecarHint">Changing the role invalidates the current link and creates a fresh one for {invite.recipient || 'the recipient'}.</p>
          </section>
        ) : null}
        <section className="invitesSidecarSection">
          <h4>Redelivery</h4>
          <div className="invitesSidecarActions">
            <Button variant="secondary" size="sm" disabled={!canManageInvites || busyAction === `resend:${invite.id}`} onClick={() => void resend()}>
              {isEmail ? 'Resend email' : 'Send again'}
            </Button>
            <Button variant="tertiary" size="sm" disabled={!canManageInvites || busyAction === `regen:${invite.id}`} onClick={() => void invalidateAndRegenerate()}>
              {t('NLSInvitesActionInvalidateRegenerate')}
            </Button>
          </div>
        </section>
      </>
    )
  }

  const sidecarPanel = (() => {
    if (category === 'GENERATE') return null
    if (category === 'JOIN_CODES') return null
    if (category === 'FIELD_PASSES') {
      return canManageFieldPasses ? <FieldPassesSidecar model={fieldPassesModel} /> : null
    }
    if (category === 'PERMISSIONS') return null
    if (category === 'INVITES' && !selectedItem) {
      return canManageInvites ? <div className="invitesSidecarPremium">{generatePanel}</div> : null
    }
    if (!selectedItem) return null
    return (
      <div className="invitesSidecarPremium">
        {selectedItem.kind === 'PENDING'
          ? renderPendingInviteSidecar(selectedItem.invite)
          : renderPersonSidecar(selectedItem.person, selectedItem.kind)}
      </div>
    )
  })()

  const hasSidecarContent = sidecarPanel !== null

  const profileModal = profileTargetUid ? (
    <MemberProfileModal userId={profileTargetUid} onClose={() => setProfileTargetUid(null)} />
  ) : null

  const joinWithCodeDialog = (
    <JoinWithCodeDialog
      open={joinCodeDialogOpen}
      onClose={() => setJoinCodeDialogOpen(false)}
      onClaimed={(claimed) => {
        setJoinCodeDialogOpen(false)
        void refresh()
        void refreshWorkspaces?.(claimed.workspaceId)
      }}
    />
  )

  const whatsappInviteDialog = whatsappDialogPerson && whatsappInviteInfo?.sendingNumberE164 ? (
    <AppDialog
      open={Boolean(whatsappDialogPerson)}
      size="sm"
      data-testid="whatsapp-invite-dialog"
      title={t('NLSWhatsappInviteDialogTitle')}
      message={`${whatsappDialogPerson.displayName} · ${maskPhone(whatsappDialogPerson.mobileNumber)}`}
      onDismiss={() => setWhatsappDialogPerson(null)}
      actions={[
        {
          label: t('NLSWhatsappInviteDialogCopy'),
          variant: 'secondary',
          testId: 'whatsapp-copy-link-btn',
          onClick: () => {
            const url = waMeInviteLink(whatsappInviteInfo.sendingNumberE164)
            if (url) {
              void copyText(url)
                .then(() => setStatus('Link copied.'))
                .catch(() => setStatus('Copy unavailable.'))
            }
          },
        },
        ...(typeof navigator !== 'undefined' && typeof navigator.share === 'function'
          ? [
              {
                label: t('NLSWhatsappInviteDialogShare'),
                variant: 'primary' as const,
                testId: 'whatsapp-share-btn',
                onClick: async () => {
                  const url = waMeInviteLink(whatsappInviteInfo.sendingNumberE164)
                  if (url) {
                    try {
                      await navigator.share({
                        title: t('NLSWhatsappInviteDialogTitle'),
                        url,
                      })
                    } catch {
                      // user dismissed share sheet
                    }
                  }
                },
              },
            ]
          : []),
      ]}
    >
      <div className="flex flex-col items-center justify-center py-4" data-testid="whatsapp-invite-qr-container">
        {whatsappQrSvg ? (
          <div
            className="w-48 h-48 flex items-center justify-center p-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] [&>svg]:w-full [&>svg]:h-full"
            data-testid="whatsapp-invite-qr-svg"
            dangerouslySetInnerHTML={{ __html: whatsappQrSvg }}
          />
        ) : (
          <div className="w-48 h-48 rounded-lg bg-[var(--surface-raised)] animate-pulse" />
        )}
      </div>
    </AppDialog>
  ) : null

  if (isMobile) {
    return (
      <>
      <section className="invitesHub invitesHub--mobile railPageCard flex flex-col gap-3 text-[var(--text-strong)]" data-testid="invites-hub-v2">
        {!mobileHideOwnHeader || !isPinnedMobileRail ? (
          <header className="invitesHubHeader--mobile">
            <div>
              <p className="settingsEyebrow">{t('NLSInvitesHubEyebrow')}</p>
              <h1>{activeWorkspace.name}</h1>
              <p>{t('NLSInvitesHubBody')}</p>
            </div>
            <div className="invitesMobileStatRow">
              {mobileCapacityStats}
              <Button
                data-testid="invites-hub-join-with-code-btn-mobile"
                variant="secondary"
                size="sm"
                onClick={() => setJoinCodeDialogOpen(true)}
              >
                {t('NLSJoinWithCodeAction')}
              </Button>
            </div>
          </header>
        ) : null}

        {statusBanner}

        {!isPinnedMobileRail && categoryEntries.length > 0 ? (
          <div className="invitesMobileSegmented" role="group" aria-label={t('NLSInvitesAriaMembersCategory')}>
            {categoryEntries.map(([key, count]) => (
              <Pressable
                key={key}
                aria-pressed={category === key}
                className={`invitesMobileSegment ${category === key ? 'isActive' : ''}`}
                onClick={() => selectCategory(key)}
              >
                <strong>{getCategoryLabel(key, t)}</strong>
                <em>{key === 'INVITES' ? `${count}/${maxPending}` : key === 'FIELD_PASSES' ? 'Burner' : key === 'PERMISSIONS' ? 'Policy' : key === 'JOIN_CODES' ? t('NLSInvitesCategoryJoinCodesTag') : `${count}/${maxRoster}`}</em>
              </Pressable>
            ))}
          </div>
        ) : null}

        <div className="invitesMobileStage">{mainList}</div>

        {!isPinnedMobileRail && hasSidecarContent ? (
          <div className="invitesMobileActionBar">
            <Button variant="primary" size={isMobile ? 'lg' : 'md'} onClick={() => setSheetOpen(true)} data-testid="invites-mobile-manage">{t('NLSInvitesActionManageInvites')}</Button>
          </div>
        ) : null}

        <BottomSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t('NLSInvitesActionManageInvites')}
          className="invitesMobileSheet"
        >
          <div className="invitesMobileSheetBody" data-testid="invites-sidecar">
            {sidecarPanel}
          </div>
        </BottomSheet>
      </section>
      {profileModal}
      {joinWithCodeDialog}
      {whatsappInviteDialog}
      </>
    )
  }

  const membersHeaderTitleByCategory: Record<CategoryKey, string> = {
    GENERATE: 'Send invite',
    INVITES: 'Pending invites',
    MEMBERS: 'Active members',
    HELPERS: 'Active helpers',
    FIELD_PASSES: 'Field passes',
    PERMISSIONS: 'Permissions',
    JOIN_CODES: 'Join codes',
  }
  return (
    <>
    <RailPageShell
      variant="three-column"
      ariaLabel="Members hub"
      className="invitesHub text-[var(--text-strong)]"
      identity={{ badge: 'M', title: 'Members', subtitle: 'Access & roles' }}
      leftRail={(
        <div data-testid="invites-hub-v2-rail" className="flex flex-col gap-3">
          {categoryNav}
          {canViewPermissionsRail && onOpenWorkspaceSettings ? (
            <div className="pt-2">
              <SettingsLinkChip
                label={t('NLSTeamSectionPermissions')}
                onClick={onOpenWorkspaceSettings}
                testId="invites-rail-permissions-settings-chip"
              />
            </div>
          ) : null}
        </div>
      )}
      middleHeader={{
        title: membersHeaderTitleByCategory[category] ?? CATEGORY_META[category].label,
        actions: (
          <Button
            data-testid="invites-hub-join-with-code-btn"
            variant="secondary"
            size="sm"
            onClick={() => setJoinCodeDialogOpen(true)}
          >
            {t('NLSJoinWithCodeAction')}
          </Button>
        ),
      }}
      middleStage={(
        <main className="sideNavPane flex min-h-[520px] flex-col gap-4">
          <SeatsStrip workspaceId={activeWorkspaceId} />
          {statusBanner}
          <section className="min-h-[420px] hubStagePanel">
            {mainList}
          </section>
        </main>
      )}
      rightSidecar={(
        <aside className="sideNavPane" data-testid="invites-sidecar">
          {hasSidecarContent ? sidecarPanel : (
            <div className="railPageShell__card p-4 text-center text-[var(--muted)] text-sm">
              Select an item to see details
            </div>
          )}
        </aside>
      )}
    />
    {profileModal}
    {activeWorkspace ? (
      <ControlProfilesManager
        workspaceId={activeWorkspace.id}
        members={members}
        isOpen={profilesManagerOpen}
        onClose={() => setProfilesManagerOpen(false)}
        canManage={canManageMembers}
      />
    ) : null}
    {pendingRemoval ? (
      <ConfirmActionModal
        open={Boolean(pendingRemoval)}
        onClose={() => setPendingRemoval(null)}
        onConfirm={handleConfirmRemoval}
        title={
          pendingRemoval.kind === 'HELPER'
            ? t('NLSInvitesConfirmRemoveHelperTitle')
            : t('NLSInvitesConfirmRemoveMemberTitle')
        }
        body={
          pendingRemoval.kind === 'HELPER'
            ? t('NLSInvitesConfirmRemoveHelperBody', {
                name: pendingRemoval.person.displayName || pendingRemoval.person.email || 'This helper',
              })
            : t('NLSInvitesConfirmRemoveMemberBody', {
                name: pendingRemoval.person.displayName || pendingRemoval.person.email || 'This member',
              })
        }
        confirmLabel={
          pendingRemoval.kind === 'HELPER'
            ? t('NLSInvitesRemoveHelperAction')
            : t('NLSInvitesRemoveMemberAction')
        }
        cancelLabel={t('NLSConfirmActionCancel')}
        danger
        busy={busyAction === `remove-member:${pendingRemoval.person.id}`}
      />
    ) : null}
    {joinWithCodeDialog}
    {whatsappInviteDialog}
    {leavingWorkspace ? (
      <LeaveWorkspaceConfirmModal
        open={Boolean(leavingWorkspace)}
        workspaceId={leavingWorkspace.id}
        workspaceName={leavingWorkspace.name}
        onClose={() => setLeavingWorkspace(null)}
        onConfirm={async () => {
          await leaveWorkspaceAndPurgeOutbox({ workspaceId: leavingWorkspace.id })
        }}
        onLeft={handleLeftWorkspace}
        onNavigateToOwners={() => setLeavingWorkspace(null)}
      />
    ) : null}
    {pendingAppAccessChange ? (
      <ConfirmDialog
        title={t('NLSHelperAppAccessConfirmTitle')}
        body={t('NLSHelperAppAccessConfirmBody')}
        confirmLabel="Confirm"
        onConfirm={async () => {
          const target = pendingAppAccessChange
          setPendingAppAccessChange(null)
          await executeSetAppAccess(target.person, false)
        }}
        onCancel={() => {
          setPendingAppAccessChange(null)
        }}
      />
    ) : null}
    </>
  )
})

export default InvitesHubV2
